import * as React from "react";
import { cn } from "@/lib/utils";

export type SparklineTone = "success" | "warning" | "danger" | "brand" | "neutral";

const STROKE_VAR: Record<SparklineTone, string> = {
  success: "var(--rd-success-500)",
  warning: "var(--rd-warning-500)",
  danger: "var(--rd-danger-500)",
  brand: "var(--brand-500)",
  neutral: "var(--text-tertiary)",
};

const FILL_VAR: Record<SparklineTone, string> = {
  success: "var(--rd-success-100)",
  warning: "var(--rd-warning-100)",
  danger: "var(--rd-danger-100)",
  brand: "var(--brand-100)",
  neutral: "var(--bg-muted)",
};

export interface SparklineProps {
  data: number[];
  w?: number;
  h?: number;
  strokeWidth?: number;
  tone?: SparklineTone;
  fill?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function Sparkline({
  data,
  w = 120,
  h = 40,
  strokeWidth = 1.6,
  tone = "brand",
  fill = false,
  className,
  ariaLabel,
}: SparklineProps): React.ReactElement {
  if (data.length === 0) {
    return <SparklineSkeleton w={w} h={h} className={className} />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? w / (data.length - 1) : 0;

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = h - ((v - min) / range) * h;
    return [x, y] as const;
  });

  const path = points.map(([x, y], i) => (i === 0 ? `M ${x},${y}` : `L ${x},${y}`)).join(" ");

  const areaPath = `${path} L ${w},${h} L 0,${h} Z`;

  return (
    <svg
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={cn("block", className)}
    >
      {fill && <path d={areaPath} fill={FILL_VAR[tone]} />}
      <path
        d={path}
        fill="none"
        stroke={STROKE_VAR[tone]}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SparklineSkeleton({
  w = 120,
  h = 40,
  bars = 12,
  className,
}: {
  w?: number;
  h?: number;
  bars?: number;
  className?: string;
}): React.ReactElement {
  const barW = w / (bars * 1.5);
  const gap = barW / 2;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      className={cn("block", className)}
    >
      {Array.from({ length: bars }).map((_, i) => {
        const x = i * (barW + gap);
        const height = h * 0.5;
        return (
          <rect
            key={i}
            x={x}
            y={(h - height) / 2}
            width={barW}
            height={height}
            rx={1}
            fill="var(--bg-muted)"
            className="[animation-delay:var(--bar-delay)] motion-safe:animate-pulse-dot"
            style={{ "--bar-delay": `${i * 60}ms` } as React.CSSProperties}
          />
        );
      })}
    </svg>
  );
}
